import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { api } from '../../shared/api/client'
import { useAuthStore } from '../../stores/authStore'
import { RequireAccess } from '../../app/RequireAccess'
import { PageLoader } from '../../app/PageLoader'
import { Button, Card } from '../../components/ui'

export function AgentAuthGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const authenticated = useAuthStore((state) => state.isAuthenticated)
  const token = useAuthStore((state) => state.token)
  const setUser = useAuthStore((state) => state.setUser)
  const logout = useAuthStore((state) => state.logout)
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!authenticated || !token) return
    const controller = new AbortController()
    setError(false)
    // Стандартный клиент не разрывает сессию сам для этого запроса: сохраняем OAuth-параметры при 401.
    api.get('/auth/me', { signal: controller.signal }).then((response) => {
      if (controller.signal.aborted) return
      const user = response.data?.data?.user ?? response.data?.user
      if (!user || !Array.isArray(user.permissions)) throw new Error('Не удалось загрузить права')
      setUser(user)
      setVerifiedToken(token)
    }).catch((failure) => {
      if (controller.signal.aborted) return
      if (failure?.response?.status === 401) logout()
      else setError(true)
    })
    return () => controller.abort()
  }, [authenticated, token, setUser, logout, attempt])

  if (!authenticated || !token) {
    const returnTo = `${location.pathname}${location.search}`
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />
  }
  if (error) return <Card padding role="alert"><p className="mb-4 text-zinc-700 dark:text-zinc-300">Не удалось проверить сессию. Попробуйте ещё раз.</p><Button onClick={() => setAttempt((value) => value + 1)}>Повторить</Button></Card>
  if (verifiedToken !== token) return <PageLoader />
  // Business grants live independently of the generator's owner/admin gate.
  // Backend verifies exact resource, scopes, live grants and cabinets on consent.
  let business = false
  try { business = new URL(new URLSearchParams(location.search).get('resource') || '').pathname === '/mcp/business' } catch { /* generator gate */ }
  return business ? <>{children}</> : <RequireAccess>{children}</RequireAccess>
}
